import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, MIN_PASSWORD_LENGTH, PASSWORD_HINT } from '../api.js';

/**
 * The address on the account and the password, from the panel behind the
 * username in the masthead.
 *
 * A forgotten password is reset from the sign in screen, by email. This
 * panel is for someone already in: it is where an account that predates
 * email gets an address, and where an address is changed. Where neither
 * works -- no mail configured, or the mail never arrived -- the command
 * line is the way back in: `import-cli.js user password` and
 * `import-cli.js user verify`.
 */

export function Account({ username, onClose }: { username: string; onClose: () => void }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  /** What happened to the address: written outright, or a link sent to
   * prove it. Null until the button is pressed. */
  const [emailSaved, setEmailSaved] = useState<'saved' | 'sent' | null>(null);
  /** Null until the account has been read, so the box is not shown
   * ticked or unticked before anyone knows which. */
  const [turnMail, setTurnMail] = useState<boolean | null>(null);
  const [botToken, setBotToken] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nameSaved, setNameSaved] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    void api.me().then((me) => {
      setTurnMail(me.turnMail !== false);
      setFirstName(me.firstName ?? '');
      setLastName(me.lastName ?? '');
      setEmail(me.email ?? '');
    }).catch(() => {});
    void api.botToken().then((t) => setBotToken(t.token)).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Checked here as well as on the server so a mistyped confirmation is
  // caught without a round trip.
  const mismatch = confirm.length > 0 && next !== confirm;
  const canSubmit = current.length > 0 && next.length >= MIN_PASSWORD_LENGTH
    && next === confirm && !busy;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.changePassword(current, next);
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change the password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="help-backdrop" onClick={onClose} role="presentation">
      <div
        className="help-panel plate"
        role="dialog"
        aria-modal="true"
        aria-label="Account"
        tabIndex={-1}
        ref={panel}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hazard hazard-rule" role="presentation" />

        <div className="help-body">
          <div className="spread">
            <h3>Account</h3>
            <button className="secondary" onClick={onClose}>Close</button>
          </div>

          <p className="mono muted">Signed in as {username}</p>

          {/* Accounts made before email existed have none, and cannot
              reset their own password without one. */}
          <div>
            <label htmlFor="account-email">Email address</label>
            <input
              id="account-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <div className="row" style={{ marginTop: '0.4rem' }}>
              <button
                className="secondary tiny"
                disabled={!email.includes('@') || busy}
                onClick={() => {
                  setBusy(true);
                  setError(null);
                  void api.setEmail(email)
                    .then((answer) => setEmailSaved(answer.pending ? 'sent' : 'saved'))
                    .catch((err) => setError(
                      err instanceof ApiError ? err.message : 'Could not save that.',
                    ))
                    .finally(() => setBusy(false));
                }}
              >
                Save address
              </button>
              {emailSaved === 'saved' && <span className="mono muted">saved</span>}
            </div>
            {/* The address is not on the account until the link is opened,
                so the old one -- if there was one -- still works until
                then. Saying so stops this looking like it failed. */}
            {emailSaved === 'sent' && (
              <p className="notice ok" style={{ marginTop: '0.4rem' }}>
                A confirmation link is on its way to {email}. The address is
                yours once you open it.
              </p>
            )}
          </div>

          {/* Both optional. */}
          <div>
            <label htmlFor="account-first-name">First name</label>
            <input
              id="account-first-name"
              autoComplete="given-name"
              maxLength={64}
              value={firstName}
              onChange={(e) => { setFirstName(e.target.value); setNameSaved(false); }}
            />
            <label htmlFor="account-last-name" style={{ marginTop: '0.4rem' }}>Last name</label>
            <input
              id="account-last-name"
              autoComplete="family-name"
              maxLength={64}
              value={lastName}
              onChange={(e) => { setLastName(e.target.value); setNameSaved(false); }}
            />
            <div className="row" style={{ marginTop: '0.4rem' }}>
              <button
                className="secondary tiny"
                disabled={busy}
                onClick={() => {
                  setBusy(true);
                  setError(null);
                  void api.setName(firstName, lastName)
                    .then(() => setNameSaved(true))
                    .catch((err) => setError(
                      err instanceof ApiError ? err.message : 'Could not save that.',
                    ))
                    .finally(() => setBusy(false));
                }}
              >
                Save name
              </button>
              {nameSaved && <span className="mono muted">saved</span>}
            </div>
          </div>

          <div>
            <label htmlFor="game-setups">Game setups</label>
            <button
              id="game-setups"
              className="secondary"
              onClick={() => { onClose(); navigate('/setups'); }}
            >
              Manage setups
            </button>
          </div>

          {botToken !== null && (
            <div>
              <label htmlFor="bot-token">Bot token</label>
              <input
                id="bot-token"
                className="mono"
                readOnly
                value={botToken}
                onFocus={(e) => e.target.select()}
              />
              <div className="row" style={{ marginTop: '0.4rem' }}>
                <button
                  className="secondary tiny"
                  onClick={() => void navigator.clipboard?.writeText(botToken)}
                >
                  Copy
                </button>
                <button
                  className="secondary tiny"
                  title="Save as bot.token beside rallyx-bot.exe, and the bot reads it from there"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(new Blob([`${botToken}\n`], { type: 'text/plain' }));
                    link.download = 'bot.token';
                    link.click();
                    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
                  }}
                >
                  Save
                </button>
                <button
                  className="secondary tiny"
                  disabled={busy}
                  onClick={() => {
                    if (!window.confirm('Make a new bot token? The old one stops working, and so does every bot running with it.')) return;
                    setBusy(true);
                    setError(null);
                    void api.regenerateBotToken()
                      .then((t) => setBotToken(t.token))
                      .catch((err) => setError(
                        err instanceof ApiError ? err.message : 'Could not make a new token.',
                      ))
                      .finally(() => setBusy(false));
                  }}
                >
                  Regenerate
                </button>
              </div>
              <p className="mono muted" style={{ fontSize: '0.75rem', marginTop: '0.3rem' }}>
                Runs the portable bot in games you host:
                rallyx-bot &lt;bot&gt; &lt;game&gt; &lt;token&gt;. Save the
                bot.token file in the same folder as rallyx-bot.exe to
                automatically include the token.
              </p>
            </div>
          )}

          {turnMail !== null && (
            <label className="powerdown">
              <input
                type="checkbox"
                checked={turnMail}
                disabled={busy}
                onChange={(e) => {
                  const on = e.target.checked;
                  setBusy(true);
                  setError(null);
                  void api.setTurnMail(on)
                    .then(() => setTurnMail(on))
                    .catch((err) => setError(
                      err instanceof ApiError ? err.message : 'Could not save that.',
                    ))
                    .finally(() => setBusy(false));
                }}
              />
              <span>Email me when a turn-mode game is waiting on me</span>
            </label>
          )}

          {done ? (
            <>
              <p className="notice ok">
                Password changed. Any other browser signed in as you has been
                signed out.
              </p>
              <button onClick={onClose}>Done</button>
            </>
          ) : (
            <>
              <div>
                <label htmlFor="current-password">Current password</label>
                <input
                  id="current-password"
                  type="password"
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="new-password">New password</label>
                <input
                  id="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                />
                <p className="mono muted" style={{ fontSize: '0.75rem', marginTop: '0.3rem' }}>
                  {PASSWORD_HINT}
                </p>
              </div>

              <div>
                <label htmlFor="confirm-password">New password again</label>
                <input
                  id="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) void submit(); }}
                />
              </div>

              {mismatch && <p className="notice">Those two do not match.</p>}
              {error && <p className="notice">{error}</p>}

              <button onClick={() => void submit()} disabled={!canSubmit}>
                Change password
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
